import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { CalendarDots, MapPin, CheckCircle, XCircle, QuestionMark, Users } from "@phosphor-icons/react";

const rsvpToneMap = {
  yes: "bg-ember text-navy border-ember",
  no: "bg-navy/5 text-navy border-navy/20",
  maybe: "bg-white text-navy border-navy/40",
};

export default function MemberEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await memberApi.get("/member/events");
      setEvents(data.events || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doRsvp = async (eventId, status) => {
    setBusy(`${eventId}:${status}`);
    try {
      await memberApi.post(`/member/events/${eventId}/rsvp`, { status });
      toast.success(`RSVP recorded: ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "RSVP failed");
    } finally {
      setBusy("");
    }
  };

  if (loading) return <div className="text-navy/60" data-testid="portal-events-loading">Loading…</div>;

  return (
    <div data-testid="portal-events-page">
      <div className="mb-6">
        <div className="overline mb-2">Events</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Upcoming meetings</h1>
        <p className="text-navy/70 mt-1 text-sm">RSVP directly — the operator sees your response in real time.</p>
      </div>

      {events.length === 0 ? (
        <div className="border border-dashed border-black/15 p-10 text-center text-navy/60" data-testid="portal-events-empty">
          No events are scheduled yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="portal-events-list">
          {events.map((e) => (
            <div key={e.id} className="bg-white border border-black/10 p-6" data-testid={`portal-event-${e.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ember">
                    <CalendarDots size={14} /> {e.date}
                  </div>
                  <div className="font-display font-bold text-xl text-navy mt-1">{e.title}</div>
                  <div className="text-sm text-navy/70 mt-1 flex items-center gap-1.5">
                    <MapPin size={12} /> {e.location}
                  </div>
                  {e.description && <p className="mt-3 text-sm text-navy/80 leading-relaxed">{e.description}</p>}
                  <div className="mt-3 flex items-center gap-3 text-xs text-navy/60">
                    <span className="flex items-center gap-1" data-testid={`portal-event-${e.id}-yescount`}>
                      <Users size={12} /> {e.rsvp_yes_count || 0} attending
                    </span>
                    {e.capacity > 0 && <span>· capacity {e.capacity}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-navy/50">Your RSVP</div>
                  <div className="flex gap-1.5" data-testid={`portal-event-${e.id}-rsvp-group`}>
                    {[
                      { key: "yes", label: "Yes", icon: CheckCircle },
                      { key: "maybe", label: "Maybe", icon: QuestionMark },
                      { key: "no", label: "No", icon: XCircle },
                    ].map(({ key, label, icon: Icon }) => {
                      const active = e.my_rsvp === key;
                      return (
                        <button
                          key={key}
                          onClick={() => doRsvp(e.id, key)}
                          disabled={busy === `${e.id}:${key}`}
                          data-testid={`portal-event-${e.id}-rsvp-${key}`}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs uppercase tracking-widest border ${active ? rsvpToneMap[key] : "border-black/15 text-navy/70 hover:border-navy"}`}
                        >
                          <Icon size={12} /> {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
